// El resultado de un partido: al mejor de 3 sets, con súper tiebreak en el 3º
// (decidido por Marcos el 2026-09-30, igual en todas las fases).
//
// Un solo formato a propósito: la validación es fija y la carga es rápida.
// Si algún día cambia (un set largo, games fijos), se cambia acá.

import { bien, mal, type Resultado } from './validar';

export type Lado = 'a' | 'b';

/** Un set: [games de A, games de B]. */
export type Set = readonly [number, number];

/**
 * Lo que se guarda. `wo` = ganó por W.O. ese lado (la otra pareja no se
 * presentó); en ese caso no hay sets.
 */
export type Marcador =
  | { wo: null; sets: readonly [Set, Set]; tb: Set | null }
  | { wo: Lado; sets: null; tb: null };

/** Un set normal bien jugado: 6-0 a 6-4, 7-5 o 7-6. */
export function setValido([a, b]: Set): boolean {
  if (!Number.isInteger(a) || !Number.isInteger(b) || a < 0 || b < 0) return false;
  const alto = Math.max(a, b);
  const bajo = Math.min(a, b);
  if (alto === 6) return bajo <= 4;
  if (alto === 7) return bajo === 5 || bajo === 6;
  return false;
}

/** Súper tiebreak: a 10, con 2 de diferencia (10-8, 11-9, 14-12...). */
export function superTiebreakValido([a, b]: Set): boolean {
  if (!Number.isInteger(a) || !Number.isInteger(b) || a < 0 || b < 0) return false;
  const alto = Math.max(a, b);
  const bajo = Math.min(a, b);
  if (alto < 10) return false;
  return alto === 10 ? bajo <= 8 : alto - bajo === 2;
}

const ganadorDeSet = ([a, b]: Set): Lado => (a > b ? 'a' : 'b');

/** Valida lo que manda el organizador y lo deja en la forma que se guarda. */
export function validarMarcador(cuerpo: unknown): Resultado<Marcador> {
  const c = typeof cuerpo === 'object' && cuerpo !== null ? (cuerpo as Record<string, unknown>) : {};

  if (c.wo === 'a' || c.wo === 'b') return bien({ wo: c.wo, sets: null, tb: null });
  if (c.wo !== undefined && c.wo !== null) return mal('El W.O. es para la pareja A o la B.');

  const esSet = (x: unknown): x is Set =>
    Array.isArray(x) && x.length === 2 && x.every((n) => typeof n === 'number');

  const sets = c.sets;
  if (!Array.isArray(sets) || sets.length !== 2 || !sets.every(esSet)) return mal('Faltan los dos sets.');
  const [s1, s2] = sets as [Set, Set];
  if (!setValido(s1)) return mal(`El primer set (${s1[0]}-${s1[1]}) no es un resultado posible.`);
  if (!setValido(s2)) return mal(`El segundo set (${s2[0]}-${s2[1]}) no es un resultado posible.`);

  const unoYUno = ganadorDeSet(s1) !== ganadorDeSet(s2);
  const tb = c.tb ?? null;
  if (!unoYUno) {
    if (tb !== null) return mal('Con 2-0 en sets no hay súper tiebreak.');
    return bien({ wo: null, sets: [s1, s2], tb: null });
  }
  if (!esSet(tb)) return mal('Quedaron 1 a 1: falta el súper tiebreak.');
  if (!superTiebreakValido(tb)) return mal(`El súper tiebreak (${tb[0]}-${tb[1]}) no es posible: es a 10, con 2 de diferencia.`);
  return bien({ wo: null, sets: [s1, s2], tb });
}

/** Quién ganó. */
export function ganador(m: Marcador): Lado {
  if (m.wo) return m.wo;
  if (m.tb) return ganadorDeSet(m.tb);
  return ganadorDeSet(m.sets[0]);
}

/**
 * Lo que suma para la tabla, desde el lado A.
 *
 * - El W.O. cuenta como 6-0 6-0 para el que se presentó.
 * - El súper tiebreak cuenta como un set y **1 game** para el que lo ganó: si
 *   sumara sus puntos, un 10-8 inflaría la diferencia de games.
 */
export function cuenta(m: Marcador): { setsA: number; setsB: number; gamesA: number; gamesB: number } {
  if (m.wo) {
    return m.wo === 'a' ? { setsA: 2, setsB: 0, gamesA: 12, gamesB: 0 } : { setsA: 0, setsB: 2, gamesA: 0, gamesB: 12 };
  }
  let setsA = 0;
  let setsB = 0;
  let gamesA = 0;
  let gamesB = 0;
  for (const [a, b] of m.sets) {
    gamesA += a;
    gamesB += b;
    if (a > b) setsA++;
    else setsB++;
  }
  if (m.tb) {
    if (m.tb[0] > m.tb[1]) {
      setsA++;
      gamesA++;
    } else {
      setsB++;
      gamesB++;
    }
  }
  return { setsA, setsB, gamesA, gamesB };
}

/** "6-4 3-6 10-8" o "W.O." — para mostrar. */
export function marcadorEnTexto(m: Marcador, desde: Lado = 'a'): string {
  if (m.wo) return 'W.O.';
  const t = ([a, b]: Set) => (desde === 'a' ? `${a}-${b}` : `${b}-${a}`);
  return [...m.sets.map(t), ...(m.tb ? [t(m.tb)] : [])].join(' ');
}
